// @ts-check
/* eslint-env browser */

/** @typedef {'csv' | 'geojson' | 'shapefile' | 'geotiff'} ExportFormat */
/** @typedef {'all-complete' | 'partial-incomplete' | 'all-incomplete' | 'unknown'} SelectionState */

const WARNINGS = {
  'partial-incomplete': 'Some of your selected items are still processing or have failed and cannot be exported. They will not be included in your exported files.',
  'all-incomplete': 'The selected items are still processing or have failed and cannot be exported.'
}

/**
 * @param {HTMLElement} host
 * @param {string[]} selectedIds
 * @returns {string[]}
 */
export function getCompletedJobIds (host, selectedIds) {
  return selectedIds.filter((id) => {
    const escaped = CSS.escape(id)
    return host.querySelector(
      `tr[data-job-id="${escaped}"] status-checker[status="completed"], status-checker[job-id="${escaped}"][status="completed"]`
    )
  })
}

/**
 * @param {string[]} jobIds
 * @param {HTMLElement | null} [host]
 * @returns {SelectionState}
 */
export function deriveSelectionState (jobIds, host = null) {
  if (!host || jobIds.length === 0) {
    return 'unknown'
  }

  const completedIds = getCompletedJobIds(host, jobIds)
  if (completedIds.length === 0) {
    return 'all-incomplete'
  }
  if (completedIds.length < jobIds.length) {
    return 'partial-incomplete'
  }
  return 'all-complete'
}

/**
 * @param {HTMLElement} dialogRoot
 * @param {object} state
 * @param {ExportFormat} state.format
 * @param {SelectionState} state.selection
 * @param {string} [state.error]
 * @param {boolean} [state.busy]
 */
export function applyDialogState (dialogRoot, { format, selection, error = '', busy = false }) {
  dialogRoot.dataset.format = format
  dialogRoot.dataset.selection = selection
  dialogRoot.dataset.error = error ? 'true' : 'false'
  dialogRoot.dataset.busy = busy ? 'true' : 'false'

  const warningText = dialogRoot.querySelector('.modal-dialog__export-warning-text')
  if (warningText) {
    warningText.textContent = WARNINGS[selection] ?? ''
  }

  const errorMessage = /** @type {HTMLElement | null} */ (
    dialogRoot.querySelector('.modal-dialog__export-error-message')
  )
  if (errorMessage) {
    errorMessage.textContent = error
    if (error) {
      errorMessage.focus()
    }
  }
}

/**
 * @param {HTMLElement} container
 * @param {string} message
 */
export function setDialogError (container, message) {
  if (!message) {
    container.innerHTML = ''
    container.hidden = true
    return
  }

  container.hidden = false
  container.innerHTML = `<p class="govuk-error-message govuk-!-margin-bottom-4" tabindex="-1">${message}</p>`
  const errorEl = /** @type {HTMLElement | null} */ (container.querySelector('.govuk-error-message'))
  errorEl?.focus()
}

/**
 * @param {number} ms
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * @param {string} url
 */
function downloadFromUrl (url) {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.target = '_blank'
  anchor.rel = 'noopener'
  anchor.download = ''
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

/**
 * Groups job IDs by document type so each export request only contains one type.
 * The backend rejects mixed-type selections in a single POST /extract/export call.
 *
 * @param {HTMLElement | null | undefined} host
 * @param {string[]} jobIds
 * @returns {Map<string, string[]>}
 */
function groupJobIdsByType (host, jobIds) {
  /** @type {Map<string, string[]>} */
  const groups = new Map()

  for (const id of jobIds) {
    let jobType = ''
    if (host) {
      const escaped = CSS.escape(id)
      const row = host.querySelector(`tr[data-job-id="${escaped}"]`)
      jobType = row?.getAttribute('data-job-type') ?? ''
    }
    const key = jobType || 'unknown'
    const existing = groups.get(key) ?? []
    existing.push(id)
    groups.set(key, existing)
  }

  return groups
}

/**
 * @param {object} payload
 * @param {string[]} payload.jobIds
 * @param {ExportFormat} payload.fileType
 * @param {string} payload.crs
 * @param {string} [payload.grouping]
 * @returns {Promise<{ url?: string, error?: string }>}
 */
async function requestBulkExport ({ jobIds, fileType, crs, grouping }) {
  try {
    const payload = {
      job_ids: jobIds,
      file_type: fileType,
      crs
    }
    if (grouping) {
      payload.grouping = grouping
    }

    const response = await fetch('/api/jobs/export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      let message = 'Could not export extractions. Please try again.'
      try {
        const errorBody = await response.json()
        if (errorBody.error) {
          message = errorBody.error
        }
      } catch {
        // use default message
      }
      return { error: message }
    }

    const { url } = await response.json()
    if (!url) {
      return { error: 'Could not export extractions. Please try again.' }
    }
    return { url }
  } catch {
    return { error: 'Could not export extractions. Please try again.' }
  }
}

/**
 * @returns {string}
 */
function buildExportDialogHtml () {
  return `
    <modal-dialog>
      <dialog
        class="modal-dialog modal-dialog--export"
        data-format="geojson"
        data-selection="unknown"
        data-error="false"
        data-busy="false"
      >
        <h2 class="modal-dialog__title govuk-heading-m">Export settings</h2>
        <div class="modal-dialog__export-warning">
          <div class="govuk-warning-text govuk-!-margin-bottom-4">
            <span class="govuk-warning-text__icon" aria-hidden="true">!</span>
            <strong class="govuk-warning-text__text">
              <span class="govuk-visually-hidden">Warning</span>
              <span class="modal-dialog__export-warning-text"></span>
            </strong>
          </div>
        </div>
        <div class="modal-dialog__export-error" role="alert" aria-live="assertive">
          <p class="govuk-error-message modal-dialog__export-error-message govuk-!-margin-bottom-4" tabindex="-1"></p>
        </div>
        <div class="modal-dialog__export-fields">
          <div class="govuk-form-group">
            <label class="govuk-label" for="extraction-export-format">File type</label>
            <select class="govuk-select" id="extraction-export-format" name="extraction-export-format">
              <option value="csv">CSV</option>
              <option value="geojson" selected>GeoJSON</option>
              <option value="shapefile">Shapefile</option>
              <option value="geotiff">GeoTIFF</option>
            </select>
          </div>
          <div class="modal-dialog__export-crs">
            <div class="govuk-form-group">
              <label class="govuk-label" for="extraction-export-crs">Coordinate reference system</label>
              <select class="govuk-select" id="extraction-export-crs" name="extraction-export-crs">
                <option value="4326" selected>WGS84 (EPSG:4326)</option>
                <option value="27700">British National Grid (EPSG:27700)</option>
              </select>
            </div>
          </div>
          <div class="modal-dialog__export-grouping">
            <div class="govuk-form-group">
              <label class="govuk-label" for="extraction-export-grouping">Export as</label>
              <select class="govuk-select" id="extraction-export-grouping" name="extraction-export-grouping">
                <option value="batch" selected>One file (all items combined)</option>
                <option value="individual">Separate files (one per item)</option>
              </select>
            </div>
          </div>
        </div>
        <div class="govuk-button-group">
          <button type="button" class="govuk-button" data-action="confirm" data-async-confirm>Export</button>
          <button type="button" class="govuk-button govuk-button--secondary" data-action="cancel">Cancel</button>
        </div>
      </dialog>
    </modal-dialog>
  `
}

/**
 * @param {HTMLSelectElement} formatSelect
 * @param {ExportFormat[] | undefined} formats
 */
function applyFormatFilter (formatSelect, formats) {
  if (!formats || formats.length === 0) {
    return
  }

  const allowed = new Set(formats)
  for (const option of [...formatSelect.options]) {
    if (!allowed.has(/** @type {ExportFormat} */ (option.value))) {
      option.remove()
    }
  }

  if (!formatSelect.value && formatSelect.options.length > 0) {
    formatSelect.selectedIndex = 0
  }
}

/**
 * @param {ExportFormat} format
 * @param {HTMLSelectElement} crsSelect
 * @param {HTMLSelectElement} groupingSelect
 */
export function applyFieldAvailability (format, crsSelect, groupingSelect) {
  const enabled = format === 'geojson' || format === 'shapefile'

  if (!enabled) {
    crsSelect.value = '4326'
  }

  if (format === 'csv') {
    groupingSelect.value = 'batch'
  } else if (format === 'geotiff') {
    groupingSelect.value = 'individual'
  }

  crsSelect.disabled = !enabled
  groupingSelect.disabled = !enabled
}

/**
 * @param {HTMLElement} dialogRoot
 * @param {HTMLSelectElement} formatSelect
 * @param {HTMLSelectElement} crsSelect
 * @param {HTMLSelectElement} groupingSelect
 * @param {string[]} jobIds
 * @param {HTMLElement | null} [host]
 * @param {object} [overrides]
 * @param {string} [overrides.error]
 * @param {boolean} [overrides.busy]
 */
function syncDialogState (dialogRoot, formatSelect, crsSelect, groupingSelect, jobIds, host = null, overrides = {}) {
  const format = /** @type {ExportFormat} */ (formatSelect.value)
  applyFieldAvailability(format, crsSelect, groupingSelect)
  applyDialogState(dialogRoot, {
    format,
    selection: deriveSelectionState(jobIds, host),
    error: overrides.error ?? '',
    busy: overrides.busy ?? false
  })
}

/**
 * @param {object} options
 * @param {ExportFormat} options.format
 * @param {string} options.crs
 * @param {string} [options.grouping]
 * @param {string[]} options.jobIds
 * @param {HTMLElement} [options.host]
 * @returns {Promise<string | null>} Error message, if any
 */
export async function executeExtractionExport ({ format, crs, grouping, jobIds, host }) {
  const completedIds = host ? getCompletedJobIds(host, jobIds) : jobIds

  if (completedIds.length === 0) {
    return null
  }

  const groups = groupJobIdsByType(host ?? null, completedIds)
  /** @type {string[]} */
  const errors = []

  const useGrouping = format === 'geojson' || format === 'shapefile'

  for (const [, ids] of groups) {
    const result = await requestBulkExport({
      jobIds: ids,
      fileType: format,
      crs,
      grouping: useGrouping ? grouping : undefined
    })

    if (result.error) {
      errors.push(result.error)
      continue
    }

    if (result.url) {
      downloadFromUrl(result.url)
      await delay(300)
    }
  }

  if (errors.length === 0) {
    return null
  }

  return errors[0]
}

/**
 * @param {object} options
 * @param {string[]} options.jobIds
 * @param {ExportFormat[]} [options.formats]
 * @param {HTMLElement} [options.host]
 * @returns {void}
 */
export function openExtractionExportDialog ({ jobIds, formats, host }) {
  if (jobIds.length === 0) {
    return
  }

  const wrapper = document.createElement('div')
  wrapper.innerHTML = buildExportDialogHtml()

  const modalDialog = /** @type {HTMLElement & { close?: () => void } | null} */ (
    wrapper.firstElementChild
  )
  if (!modalDialog) {
    return
  }

  const dialogRoot = /** @type {HTMLElement | null} */ (
    modalDialog.querySelector('.modal-dialog--export')
  )
  const formatSelect = /** @type {HTMLSelectElement | null} */ (
    modalDialog.querySelector('#extraction-export-format')
  )
  const crsSelect = /** @type {HTMLSelectElement | null} */ (
    modalDialog.querySelector('#extraction-export-crs')
  )
  const groupingSelect = /** @type {HTMLSelectElement | null} */ (
    modalDialog.querySelector('#extraction-export-grouping')
  )

  if (!dialogRoot || !formatSelect || !crsSelect || !groupingSelect) {
    return
  }

  applyFormatFilter(formatSelect, formats)

  const updateState = (overrides = {}) => {
    syncDialogState(dialogRoot, formatSelect, crsSelect, groupingSelect, jobIds, host ?? null, overrides)
  }

  formatSelect.addEventListener('change', () => updateState())
  updateState()

  document.body.appendChild(modalDialog)

  let confirmInProgress = false
  const handleConfirm = async () => {
    if (confirmInProgress) {
      return
    }
    confirmInProgress = true
    updateState({ error: '', busy: true })

    const format = /** @type {ExportFormat} */ (formatSelect.value)
    const crs = crsSelect.value
    const grouping = groupingSelect.value
    const errorMessage = await executeExtractionExport({
      format,
      crs,
      grouping,
      jobIds,
      host
    })

    if (errorMessage) {
      updateState({ error: errorMessage, busy: false })
      confirmInProgress = false
      return
    }

    modalDialog.removeEventListener('confirm', handleConfirm)
    modalDialog.close?.()
  }

  modalDialog.addEventListener('confirm', handleConfirm)
}
